import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plane, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SITE, siteApi, flightUrl } from "@/lib/site";
import { FlagCityTile } from "@/components/site/FlagCityTile";

/** Page header in the Landing hero style. */
export function PageHeader({ kicker, title, lede, accent, children }) {
  return (
    <section className="fable-mesh relative overflow-hidden border-b border-border">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="max-w-3xl">
          {kicker && (
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: accent || "var(--fable)" }}>
              {kicker}
            </p>
          )}
          <h1 className="mt-3 font-serif-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">{title}</h1>
          {lede && <p className="mt-5 max-w-2xl text-lg text-muted-foreground">{lede}</p>}
          {children && <div className="mt-7">{children}</div>}
        </div>
      </div>
    </section>
  );
}

export function Section({ title, lede, alt, id, children, className = "" }) {
  return (
    <section id={id} className={alt ? "bg-muted/40" : ""}>
      <div className={`mx-auto max-w-7xl px-4 py-14 sm:px-6 ${className}`}>
        {title && <h2 className="font-serif-display text-3xl font-bold">{title}</h2>}
        {lede && <p className="mt-2 max-w-2xl text-muted-foreground">{lede}</p>}
        <div className={title || lede ? "mt-8" : ""}>{children}</div>
      </div>
    </section>
  );
}

export function InfoCard({ title, children, accent, className = "" }) {
  return (
    <div className={`rounded-2xl border border-border bg-card p-6 ${className}`}>
      {accent && <div className="mb-4 h-1.5 w-10 rounded-full" style={{ background: accent }} />}
      {title && <h3 className="text-lg font-bold">{title}</h3>}
      <div className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </div>
  );
}

export function Prose({ children, className = "" }) {
  return (
    <div
      className={`max-w-3xl text-[15px] leading-relaxed text-foreground/85 [&_h2]:mt-9 [&_h2]:font-serif-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-foreground [&_li]:mt-1.5 [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6 [&_a]:font-medium [&_a]:text-primary hover:[&_a]:underline ${className}`}
    >
      {children}
    </div>
  );
}

export function Faq({ items = SITE.faq }) {
  return (
    <Accordion type="single" collapsible className="max-w-3xl" data-testid="faq">
      {items.map((f, i) => (
        <AccordionItem key={f.q} value={`q${i}`}>
          <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
          <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

/** Early-access / provider register form → POST /api/launch/waitlist (same list the app and posters use). */
export function WaitlistForm({ interest = "customer", inline = false }) {
  const biz = interest === "provider";
  const [state, setState] = useState({ status: "idle", msg: "" });
  const submit = async (e) => {
    e.preventDefault();
    const f = e.currentTarget;
    if (f.website.value) return; // bots fill the hidden field
    const body = { email: f.email.value.trim(), interest };
    if (biz) {
      body.business_name = f.business_name.value.trim();
      if (f.area.value.trim()) body.area = f.area.value.trim();
    }
    setState({ status: "sending", msg: "" });
    try {
      const j = await siteApi.waitlist(body);
      f.reset();
      setState({ status: "done", msg: j && j.already ? "You're already on the list — we'll be in touch." : "You're on the list. We'll email you at launch." });
    } catch (err) {
      setState({ status: "error", msg: err?.message || `Something went wrong. Please email ${SITE.email}.` });
    }
  };
  const input = "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";
  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`waitlist-${interest}`}>
      <div className={inline ? "flex flex-col gap-2 sm:flex-row" : "space-y-3"}>
        <label className="sr-only" htmlFor={`wl-${interest}-email`}>Email</label>
        <input id={`wl-${interest}-email`} name="email" type="email" required autoComplete="email" placeholder="you@example.com" className={input} />
        {biz && (
          <>
            <label className="sr-only" htmlFor="wl-biz">Business name</label>
            <input id="wl-biz" name="business_name" required autoComplete="organization" placeholder="Business name" className={input} />
            <label className="sr-only" htmlFor="wl-area">Town or city</label>
            <input id="wl-area" name="area" autoComplete="address-level2" placeholder="Town or city (optional)" className={input} />
          </>
        )}
        <Button type="submit" className="rounded-full px-6" disabled={state.status === "sending"}>
          {state.status === "sending" ? "Sending…" : biz ? "Register my business" : "Notify me at launch"}
        </Button>
      </div>
      <input name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <p className="text-xs text-muted-foreground">
        We'll only use your email to tell you about the launch{biz ? " and help you get set up" : ""}. Ask us to delete it any time.{" "}
        <Link to="/legal/privacy" className="underline">Privacy Policy</Link>
      </p>
      {state.msg && (
        <p role="status" aria-live="polite" className={`text-sm font-medium ${state.status === "error" ? "text-destructive" : "text-primary"}`}>
          {state.msg}
        </p>
      )}
    </form>
  );
}

/** Flight search: opens our travel partner with the search filled in. */
export function FlightSearch({ dest }) {
  const tomorrow = useMemo(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return t.toISOString().slice(0, 10);
  }, []);
  const [f, setF] = useState({ from: "LON", to: dest?.iata || SITE.cities[0].iata, out: "", back: "", pax: "1" });
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value, ...(k === "out" && s.back && s.back < e.target.value ? { back: "" } : {}) }));
  const go = (e) => {
    e.preventDefault();
    if (f.from === f.to) return setErr("Choose a different destination.");
    setErr("");
    window.open(flightUrl(f), "_blank", "noopener");
  };
  const field = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-primary";
  const label = "mb-1 block text-xs font-semibold text-muted-foreground";
  return (
    <form onSubmit={go} className="rounded-2xl border border-border bg-card p-5" data-testid="flight-search">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1">
          <label className={label} htmlFor="fs-from">From</label>
          <select id="fs-from" value={f.from} onChange={set("from")} className={field}>
            {SITE.uk_origins.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
          </select>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className={label} htmlFor="fs-to">To</label>
          {dest ? (
            <div className={`${field} bg-muted/50`}>{dest.name}</div>
          ) : (
            <select id="fs-to" value={f.to} onChange={set("to")} className={field}>
              {SITE.cities.map((c) => <option key={c.slug} value={c.iata}>{c.name}</option>)}
            </select>
          )}
        </div>
        <div>
          <label className={label} htmlFor="fs-out">Depart</label>
          <input id="fs-out" type="date" required min={tomorrow} value={f.out} onChange={set("out")} className={field} />
        </div>
        <div>
          <label className={label} htmlFor="fs-back">Return (optional)</label>
          <input id="fs-back" type="date" min={f.out || tomorrow} value={f.back} onChange={set("back")} className={field} />
        </div>
        <div>
          <label className={label} htmlFor="fs-pax">Adults</label>
          <select id="fs-pax" value={f.pax} onChange={set("pax")} className={field}>
            {Array.from({ length: 9 }, (_, i) => <option key={i + 1}>{i + 1}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <Button type="submit" className="w-full rounded-full"><Plane size={16} className="mr-1.5" /> Search flights</Button>
        </div>
      </div>
      {err && <p className="mt-2 text-sm text-destructive">{err}</p>}
      <p className="mt-3 text-xs text-muted-foreground">
        Opens our travel partner's site, where you see live prices and book. If you book, we may earn a commission. It never changes your price.{" "}
        <Link to="/legal/affiliate-disclosure" className="underline">How this works</Link>
      </p>
    </form>
  );
}

export function OutLink({ href, children, className = "" }) {
  return (
    <a href={href} target="_blank" rel="sponsored noopener noreferrer" className={`inline-flex items-center gap-1 font-semibold text-primary hover:underline ${className}`}>
      {children} <ExternalLink size={14} />
    </a>
  );
}

export function CityTile({ c }) {
  return (
    <Link to={"/travel/" + c.slug} className="group block" data-testid={"city-" + c.slug} aria-label={c.name + " city guide"}>
      <FlagCityTile c={c} />
    </Link>
  );
}
